import {TData} from "../hooks/usePDGeothermal";
import {TChemical, TDataOilfield} from "../hooks/usePDOilfield";
import {DataPoint} from "../component/Scatterplot";
import {
    CALCULATED_FUNCTIONS, ELEMENTS,
    GEOTHERMAL_PERCENT,
    GEOTHERMAL_PRODUCTION_CONST, OILFIELD_PERCENT,
    OILFIELD_PRODUCTION_CONST, reverseChemicalElements, TCalculatedFunction,
    WELLS_AMOUNT,
    WELLS_MARKET_MULTIPLIER
} from "./consts";
import {WellsBar} from "../hooks/useWellsOilfield";
import {NirBar, TNir} from "../hooks/useNIR";


export interface IMedian {
    subGroup: string;
    y: number;
    x: number;
    group: string;
    size: number;
    cost: number;
    worldConsumption: number;
    wellProduction: number;
}

export interface IMedianNir {
    key: string;
    count: number;
    amount: number;
}

export interface ISelectValue {
    value: string;
    label: string;
}

export function getMedianOfTopPercent(
  data: TData,
  chemical: TChemical,
  percent: number,
  productionConst: number,
  wellsAmount?: number,
  calculatedFunction: TCalculatedFunction = CALCULATED_FUNCTIONS[0],
  ): DataPoint[]  {
    const result: { [key: string]: number[] } = {};

    ELEMENTS.forEach((el) => {
        result[el.label] = []
    });
    // Collect all numeric values for each key
    data.forEach(item => {
            if (item.cl) {
                result[reverseChemicalElements[item.ce]].push(item.cl);
            }
    });

    const medians: IMedian[] = [];

    let count = 0;
    // Calculate median of the top 10% values for each key and adjust the return format
    for (const [key, values] of Object.entries(result)) {
        let medianValue: number = 0;

        if (values.length > 0) {
            const sortedValues = values.sort((a, b) => b - a); // Sort values in descending order
            const top10PercentCount = Math.ceil(sortedValues.length * percent);
            const top10PercentValues = sortedValues.slice(0, top10PercentCount);
            medianValue = calculatedFunction == "mean" ? calculateMean(top10PercentValues) : calculateMedian(top10PercentValues);
        }
        const xResult = chemical.filter(({title}) => title.toLowerCase() === key.toLowerCase())[0];
        if (medianValue && xResult && xResult.production) {
        medians.push({
            subGroup: key.charAt(0).toUpperCase() + key.slice(1),
            x: Math.log(parseInt(xResult?.production || '1000')),
            y: Math.log(medianValue * productionConst * (wellsAmount || WELLS_AMOUNT)),
            cost: parseInt(xResult?.cost || '1000'),
            worldConsumption: (parseInt(xResult?.production || '1000')),
            wellProduction: (medianValue * productionConst),
            group: `${count % 4}`, // Fill this with your desired value
            size: 135031164 // Fill this with your desired value
        });
        }
        count++;
    }

    // console.log(medians);

    return medians;
}

function calculateMedian(numbers: number[]): number {
    if (numbers.length === 0) {
        throw new Error("Array cannot be empty");
    }

    // sort array of numbers
    const sortedNumbers = numbers.slice().sort((a, b) => a - b);

    const middleIndex = Math.floor(sortedNumbers.length / 2);

    // if odd number of elements return the middle
    if (sortedNumbers.length % 2 !== 0) {
        return sortedNumbers[middleIndex];
    }

    // if even number of elements return the average of the two middle elements
    return (sortedNumbers[middleIndex - 1] + sortedNumbers[middleIndex]) / 2;
}

function calculateMean(numbers: number[]): number {
    const sum = numbers.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
    return sum / numbers.length;
}

export function getWellsData(data: DataPoint[]): WellsBar[] {
    // console.log(data);
    return data.map(({
                         subGroup,
                         // x,
                         // y,
                         // group,
                         cost,
                         wellProduction}) => {
        let buffer = wellProduction * cost * WELLS_MARKET_MULTIPLIER;
        return {
            name: subGroup,
            cost: Math.log(buffer),
            price: buffer,
        }
    }).filter(item => item.price > 0 && item.cost > 0).sort((a, b) => b.cost - a.cost);
}


export function getSumOfWells(data: TData, chemical: TChemical, percent: number, productionConst: number, wellsAmount: number = WELLS_AMOUNT): DataPoint[]  {
    const result: { [key: string]: number[] } = {};

    ELEMENTS.forEach((el) => {
        result[el.label] = []
    });
    // Collect all numeric values for each key
    data.forEach(item => {
        if (item.cl) {
            result[reverseChemicalElements[item.ce]].push(item.cl);
        }
    });
    //
    // // Collect all numeric values for each key
    // data.forEach(item => {
    //     for (const [key, value] of Object.entries(item)) {
    //         if (value) {
    //             result[key].push(Number(value) * item.productionRate ? item.productionRate : 1);
    //         }
    //     }
    // });

    const medians: IMedian[] = [];
    let count = 0;

    for (const [key, values] of Object.entries(result)) {
        let medianValue: number = 0;

        if (values.length > 0) {
            const sortedValues = values.sort((a, b) => b - a); // Sort values in descending order
            medianValue = calculateMedian(sortedValues);
        }

        const xResult = chemical.filter(({title}) => title.toLowerCase() === key.toLowerCase())[0];

        if (medianValue && xResult && Math.log(medianValue * productionConst) > 0) {
            medians.push({
                subGroup: key.charAt(0).toUpperCase() + key.slice(1),
                x: Math.log(parseInt(xResult?.production || '1000')),
                y: Math.log(medianValue * productionConst * wellsAmount),
                cost: parseInt(xResult?.cost || '1000'),
                worldConsumption: (parseInt(xResult?.production || '1000')),
                wellProduction: (medianValue * productionConst),
                group: `${count % 4}`, // Fill this with your desired value
                size: 135031164 // Fill this with your desired value
            });
        }
        count++;
    }
    return medians;
}

// function calculateSum(numbers: number[]): number {
//     return numbers.reduce((accumulator, currentValue) => {
//         return accumulator + currentValue
//     });
// }

export function getMedianOfTopPercentNir(
  data: TData,
  percent: number,
  productionConst: number,
  calculatedFunction: TCalculatedFunction = CALCULATED_FUNCTIONS[0],
  ): IMedianNir[]  {
    const result: { [key: string]: number[] } = {};

    ELEMENTS.forEach((el) => {
        result[el.label] = []
    });
    // Collect all numeric values for each key
    data.forEach(item => {
        if (item.cl) {
            result[reverseChemicalElements[item.ce]].push(item.cl);
        }
    });



    const mediansNir: IMedianNir[] = [];

    // Calculate median of the top 10% values for each key and adjust the return format
    for (const [key, values] of Object.entries(result)) {
        let medianValue: number = 0;


        if (values.length > 0) {
            const sortedValues = values.sort((a, b) => b - a); // Sort values in descending order
            const top10PercentCount = Math.ceil(sortedValues.length * percent);
            const top10PercentValues = sortedValues.slice(0, top10PercentCount);
            medianValue = calculatedFunction == "mean" ? calculateMean(top10PercentValues) : calculateMedian(top10PercentValues);
            mediansNir.push({
                key: key.charAt(0).toUpperCase() + key.slice(1),
                count: top10PercentCount,
                amount: medianValue * productionConst
            });
        }
    }

    return mediansNir;
}



export const getNirData = (data: {nirData: TNir, geothermalData: TData, oilfieldData: TData}, calculatedFunction: TCalculatedFunction = CALCULATED_FUNCTIONS[0]): NirBar[] => {
    const result: NirBar[] = [];

    const geothermalNir = getMedianOfTopPercentNir(data.geothermalData, GEOTHERMAL_PERCENT, GEOTHERMAL_PRODUCTION_CONST, calculatedFunction);

    const oilfieldNir = getMedianOfTopPercentNir(data.oilfieldData, OILFIELD_PERCENT, OILFIELD_PRODUCTION_CONST, calculatedFunction);

    data.nirData.forEach(item => {
        const geothermalNirItem = geothermalNir.find(nir => nir.key === item.chemicalElement);
        const oilfieldNirItem = oilfieldNir.find(nir => nir.key === item.chemicalElement);

        let geothermalPercent = 0;
        let oilfieldPercent = 0;


        if (geothermalNirItem && oilfieldNirItem) {
            let geothermalAmount = geothermalNirItem.amount * geothermalNirItem.count;
            let oilfieldAmount = oilfieldNirItem.amount * oilfieldNirItem.count;


            geothermalPercent = (geothermalNirItem.amount * geothermalNirItem.count * item.NIR!) / item.importValue!;
            oilfieldPercent = (oilfieldNirItem.amount * oilfieldNirItem.count * item.NIR!) / item.importValue!;

            result.push({
                element: geothermalNirItem.key,
                percent: (geothermalAmount + oilfieldAmount) >= item.importValue! ? Math.ceil(item.NIR! / 2) : Math.ceil(geothermalPercent),
                type: 'geothermal'
            })

            result.push({
                element: oilfieldNirItem.key,
                percent: (geothermalAmount + oilfieldAmount) >= item.importValue! ? Math.ceil(item.NIR! / 2) : Math.ceil(oilfieldPercent),
                type: 'oilfield'
            })

            result.push({
                element: oilfieldNirItem.key,
                percent: (geothermalAmount + oilfieldAmount) >= item.importValue! ? 0 : Math.ceil(item.NIR! - oilfieldPercent - geothermalPercent),
                type: 'import'
            })
        }


        // if (geothermalNirItem && !oilfieldNirItem) {
        //     console.log(geothermalNirItem.amount * geothermalNirItem.count, " ", geothermalNirItem.count);
        //
        //     let geothermalAmount = geothermalNirItem.amount * geothermalNirItem.count;
        //     geothermalPercent = (geothermalNirItem.amount * geothermalNirItem.count * item.NIR!) / item.importValue!;
        //
        //     result.push({
        //         element: geothermalNirItem.key,
        //         percent: (geothermalAmount) >= item.importValue! ? item.NIR! : Math.ceil(geothermalPercent),
        //         type: 'geothermal'
        //     })
        //
        //     result.push({
        //         element: geothermalNirItem.key,
        //         percent: (geothermalAmount) >= item.importValue! ? 0 : Math.floor(item.NIR! - geothermalPercent),
        //         type: 'import'
        //     })
        // }
        //
        // if (oilfieldNirItem && !geothermalNirItem) {
        //     let oilfieldAmount = oilfieldNirItem.amount * oilfieldNirItem.count;
        //     console.log(oilfieldNirItem.amount * oilfieldNirItem.count, " ", oilfieldNirItem.count);
        //     oilfieldPercent = (oilfieldNirItem.amount * oilfieldNirItem.count * item.NIR!) / item.importValue!;
        //
        //     result.push({
        //         element: oilfieldNirItem.key,
        //         percent: (oilfieldAmount) >= item.importValue! ? item.NIR! : Math.ceil(oilfieldPercent),
        //         type: 'oilfield'
        //     })
        //
        //     result.push({
        //         element: oilfieldNirItem.key,
        //         percent: (oilfieldAmount) >= item.importValue! ? 0 : Math.ceil(item.NIR! - oilfieldPercent),
        //         type: 'import'
        //     })
        // }
        //
        // if (!geothermalNirItem && !oilfieldNirItem) {
        //     result.push({
        //         element: item.chemicalElement!,
        //         percent: item.NIR!,
        //         type: 'import'
        //     })
        // }
        //
        // console.log(item);

    })
    return result;

}


/**
 * Helper function to convert an array of objects to CSV
 * @param data - Array of objects to convert to CSV
 * @returns {string} CSV string
 */
export const convertToCSV = (data: any[]) => {
    if (!data || data.length === 0) return '';

    const headers = Object.keys(data[0]).filter((key) => key !== 'id');
    const csvRows = [];

    // Add the headers row
    csvRows.push(headers.join(','));

    // Add the data rows
    for (const row of data) {
        const values = headers.map((header) => {
            const value = row[header] ?? ''; // Handle null/undefined values
            return typeof value === 'string' ? `"${value.replace(/"/g, '""')}"` : value;
        });
        csvRows.push(values.join(','));
    }

    return csvRows.join('\n');
};

export const downloadCSV = (csvData: string, fileName: string) => {
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.href = url;
    link.setAttribute('download', `${fileName}.csv`);
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

